/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { PERMISSIONS } from '@squiz/dxp-auth';
import { DIALOG_SIZE_SM_MD, DialogContentProps, DialogProps, ICON_CHEVRON_RIGHT, SdsIcon } from '@squiz/sds';
import { hasPermission } from 'apps/console-core/src/Utilities/HasPermission/HasPermission';
import { useDialogStore } from 'dxpConsoleCore-mf/State';
import React, { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { COMPONENT_SERVICE_STRINGS } from '../../../../Constants/Strings.constants';
import { COMPONENT_SET_DETAILS_DATA_IDS } from '../../ComponentSetDetails.constants';
import { ComponentSetDetailsConfigsProps } from './ComponentSetDetailsConfigs.props';
import { ComponentSetHeadersForm } from './forms/ComponentSetHeadersForm/ComponentSetHeadersForm';
import { EnvironmentVariableForm } from './forms/EnvironmentVariableForm/EnvironmentVariableForm';

/**
 * Component set details configs.
 */
export const ComponentSetDetailsConfigs = (props: ComponentSetDetailsConfigsProps): JSX.Element => {
  const { t } = useTranslation();
  const canReadEnvironmentVariables = hasPermission(PERMISSIONS.dxpComponents.COMPONENT_SET_ENVIRONMENT_READ);
  const labels = {
    cannotReadEnvironmentVariables: t(COMPONENT_SERVICE_STRINGS.cannotReadEnvironmentVariables),
    environmentVariables: t(COMPONENT_SERVICE_STRINGS.environmentVariables),
    incorrectPermissionsEnvVar: t(COMPONENT_SERVICE_STRINGS.incorrectPermissionsEnvVar),
    manage: t(COMPONENT_SERVICE_STRINGS.manage),
    noVariablesSet: t(COMPONENT_SERVICE_STRINGS.noVariablesSets),
    requestHeaders: t(COMPONENT_SERVICE_STRINGS.requestHeaders),
    setConfigurations: t(COMPONENT_SERVICE_STRINGS.setConfigurations),
  };

  const { updateActiveDialog } = useDialogStore();

  const envVarDialogProps: DialogProps = {
    dialogContent: {
      renderContent: (dialogContentProps: DialogContentProps): JSX.Element => {
        return (
          <EnvironmentVariableForm
            componentSetProps={props.componentSetProps}
            dialogContentProps={dialogContentProps}
            updateComponentSetDetails={props.updateComponentSetDetails}
          />
        );
      },
    },
    dialogSize: DIALOG_SIZE_SM_MD,
    heading: labels.environmentVariables,
    showDividers: true,
    stateHandler: useDialogStore,
  };

  const headerDialogProps: DialogProps = {
    dialogContent: {
      renderContent: (dialogContentProps: DialogContentProps): JSX.Element => {
        return (
          <ComponentSetHeadersForm
            componentSetProps={props.componentSetProps}
            dialogContentProps={dialogContentProps}
            updateComponentSetDetails={props.updateComponentSetDetails}
          />
        );
      },
    },
    dialogSize: DIALOG_SIZE_SM_MD,
    heading: labels.requestHeaders,
    showDividers: true,
    stateHandler: useDialogStore,
  };

  const openDialog = (dialogType: string): void => {
    let dialogProps: DialogProps | undefined = undefined;
    if (dialogType === 'env') {
      dialogProps = envVarDialogProps;
    }
    if (dialogType === 'header') {
      dialogProps = headerDialogProps;
    }
    updateActiveDialog(dialogProps);
  };

  const openDialogKeyboardHandler = (event: KeyboardEvent<HTMLButtonElement>, dialogType: string): void => {
    // Keys which should navigate user to the component set details screen.
    const navigateKeys = ['Enter', 'Space'];
    if (navigateKeys.includes(event.code)) {
      openDialog(dialogType);
    }
  };

  // TODO: Developer experience ticket - DEVX-864 will add the functionality for environment variables that need attention
  const noEnvironmentVariables = Object.entries(props.componentSetProps.environmentVariables || {}).length === 0;
  let noEnvironmentVariablesWarning = '';

  if (!canReadEnvironmentVariables) {
    noEnvironmentVariablesWarning = labels.cannotReadEnvironmentVariables;
  } else if (noEnvironmentVariables) {
    noEnvironmentVariablesWarning = labels.noVariablesSet;
  }

  return (
    <div className="component-set-details__configs">
      <h2 className="component-set-details__configs-heading">{labels.setConfigurations}</h2>
      <div className="component-set-details__configs-list-container">
        <ul className="component-set-details__configs-list" data-id={COMPONENT_SET_DETAILS_DATA_IDS.setConfigList}>
          {/* Environment variables */}
          <button
            className="component-set-details__configs-variables"
            data-testid={COMPONENT_SET_DETAILS_DATA_IDS.openEnvVarDialogButton}
            disabled={props.disabled || !canReadEnvironmentVariables}
            onClick={(): void => openDialog('env')}
            onKeyDown={(event): void => openDialogKeyboardHandler(event, 'env')}
          >
            <span className="component-set-details__configs-variables-title">{labels.environmentVariables}</span>
            {noEnvironmentVariablesWarning && (
              <span className="component-set-details__configs-variables-set">{noEnvironmentVariablesWarning}</span>
            )}

            <div className="component-set-details__configs-manage">
              {canReadEnvironmentVariables && (
                <>
                  {labels.manage}
                  <SdsIcon svg={ICON_CHEVRON_RIGHT} />
                </>
              )}
            </div>
          </button>

          {/* Request headers */}
          <button
            className="component-set-details__configs-headers"
            data-testid={COMPONENT_SET_DETAILS_DATA_IDS.openHeadersDialogButton}
            disabled={props.disabled}
            onClick={(): void => openDialog('header')}
            onKeyDown={(event): void => openDialogKeyboardHandler(event, 'header')}
          >
            <span className="component-set-details__configs-headers-title">{labels.requestHeaders}</span>
            <div className="component-set-details__configs-manage">
              {labels.manage}
              <SdsIcon svg={ICON_CHEVRON_RIGHT} />
            </div>
          </button>
        </ul>
      </div>
    </div>
  );
};
